import styled, { keyframes } from 'styled-components';
import { screenMd, screenSm } from 'constants/styles/breakpoints';
import { HeaderContainer as BaseHeaderContainer } from 'components/modal/styles';
import { colors } from 'constants/styles/colors';

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const ContentWrapper = styled.div`
  padding: 35px 50px;
`;

export const Title = styled.div`
  font-weight: 400;
  font-size: 24px;
  line-height: 38px;
  color: ${colors.fonts.dark};
  margin-bottom: 45px;
  margin-top: 15px;
  ${screenSm} {
    margin-top: 40px;
    font-size: 32px;
  }
`;

export const AssignUsersHeader = styled(BaseHeaderContainer)<{
  assignedToAccount: boolean;
  extraHeight?: number;
}>`
  background: linear-gradient(90deg, #9333ea 0%, #7c3aed 50%, #6d28d9 100%);
  min-height: 0;

  height: ${({ assignedToAccount, extraHeight = 0 }) =>
    `${(assignedToAccount ? 109 : 186) + extraHeight}px`};

  transition: height 220ms ease;
  will-change: height;
  padding: 40px 0;
  overflow: hidden;
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 0 20px;
  > h2 {
    color: ${colors.fonts.white};
    font-size: 20px;
    font-weight: 500;
    margin: 0;
    white-space: pre-wrap;
    text-align: center;
    ${screenMd} {
      font-size: 24px;
      font-weight: 400;
      line-height: 32px;
    }
  }
`;

export const AnimatedToggleWrapper = styled.div`
  animation: ${fadeSlideIn} 250ms ease;
`;

export const SubTitle = styled.div`
  color: ${colors.dataVisualization.black.dark};
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base);
  font-weight: 900;
  font-size: 18px;
  line-height: 26px;
  padding-top: 15px;
  ${screenSm} {
    font-size: 20px;
  }
`;

export const AccountAssignRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &&& .assign-account-toggle > span:not(.ant-checkbox) {
    color: ${colors.fonts.dark};
    font-family: var(--font-family-base);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }
`;
export const AddUsersWrapper = styled.div`
  display: block;
`;

export const CourseListContainer = styled.div`
  background: ${colors.body.bg};
  border-radius: 10px;
  padding: 20px;
  flex: 1;
`;

export const CourseListTitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.dataVisualization.black.dark};
  margin-bottom: 10px;
`;

export const CourseItem = styled.div`
  font-size: 10px;
  line-height: 12px;
  color: ${colors.fonts.white};
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px 8px;
  background: ${colors.dataVisualization.black.dark};
  margin-bottom: 5px;
  border-radius: 60px;
  width: fit-content;
`;

export const SearchResultsWrapper = styled.div`
  height: 500px;
  overflow: auto;
  margin-top: 20px;
`;

export const ModalContentWrapper = styled.div`
  padding: 0 10px;
  ${screenSm} {
    padding: 0 30px;
  }
`;

export const AnimatedSection = styled.div`
  animation: ${fadeSlideIn} 180ms ease;
`;

export const UserFilterWrapper = styled.div<{ $fading?: boolean }>`
  margin-top: 16px;
  height: 300px;
  max-height: 300px;
  ${screenSm} {
    height: 260px;
    max-height: 260px;
  }
  overflow-y: auto;
  opacity: ${({ $fading }) => ($fading ? 0 : 1)};
  transition: opacity 180ms ease, height 180ms ease;
`;

export const UserTeamToggle = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(201, 187, 255, 0.3);

  padding: 5px;
  ${screenSm} {
    width: auto;
  }
`;

export const UserTeamToggleOption = styled.button<{ $active?: boolean }>`
  display: flex;
  width: 100%;
  flex: 1;
  padding: 10px 14px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  border: none;
  background: ${({ $active }) => ($active ? colors.fonts.white : 'transparent')};
  color: ${({ $active }) => ($active ? colors.fonts.dark : colors.fonts.white)};
  text-align: center;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  opacity: ${({ $active }) => ($active ? 1 : 0.8)};
  cursor: pointer;
  white-space: nowrap;
  transition: background 250ms ease, color 250ms ease, opacity 250ms ease;

  ${screenSm} {
    width: 143px;
    flex: 0 0 auto;
    padding: 12px 24px;
    font-size: 16px;
  }

  &:hover {
    opacity: 1;
  }
`;

export const AccountAssignCardWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 30px 0;
`;

export const AccountAssignCard = styled.div`
  width: 100%;
  border-radius: 7px;
  background: var(--grey-lighter-50);
  padding: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  text-align: center;
`;

export const AccountAssignIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--white);
  border-radius: 50%;

  svg {
    width: 73px;
    height: 73px;
  }
`;

export const AccountAssignTitle = styled.div`
  color: var(--black-dark);
  font-size: 16px;
  font-weight: 700;
`;

export const AccountAssignDescription = styled.div`
  color: var(--light-gray-darker);
  font-size: 14px;
  line-height: 20px;
`;

export const FooterActions = styled.div`
  padding: 16px 20px 24px;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'toggle summary'
    'button button';
  align-items: center;
  gap: 12px;
  margin-top: auto;

  ${screenSm} {
    padding: 0 30px 30px 30px;
    display: flex;
    align-items: center;
  }
`;

export const FooterToggle = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  grid-area: toggle;
`;

export const SelectedSummary = styled.div<{ $hidden?: boolean }>`
  color: ${colors.fonts.dark};
  font-family: var(--font-family-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  text-align: right;
  justify-self: end;
  align-self: center;
  ${({ $hidden }) => $hidden && 'visibility: hidden;'}
  grid-area: summary;

  ${screenSm} {
    flex: 1;
    text-align: right;
    justify-self: auto;
  }
`;

export const FooterButton = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  grid-area: button;

  > button {
    width: 100%;
  }

  ${screenSm} {
    width: auto;
    margin-left: auto;

    > button {
      width: auto;
    }
  }
`;
